(() => {
  'use strict';
  if (window.__pplR1009V2Home) return;
  window.__pplR1009V2Home = true;
  document.documentElement.classList.add('ppl-js');
  const initialized = new WeakSet();
  const query = (scope, selector) => scope.querySelector(selector);
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = () => reducedMotion() ? 'instant' : 'smooth';
  const dialogs = new WeakMap();
  // Keep keyboard focus on a usable arrow when the focused one is about to be disabled.
  function setArrows(previous, next, previousOff, nextOff) {
    if (!previousOff) previous.disabled = false;
    if (!nextOff) next.disabled = false;
    const active = document.activeElement;
    if (nextOff && active === next && !previousOff) previous.focus({ preventScroll: true });
    if (previousOff && active === previous && !nextOff) next.focus({ preventScroll: true });
    previous.disabled = previousOff;
    next.disabled = nextOff;
  }
  // Move focus to an in-page target after navigation so the next Tab continues from there.
  function moveFocus(target) {
    const el = target.tagName === 'DETAILS' ? (target.querySelector('summary') || target) : target;
    if (!el.matches('a[href],button,input,select,textarea,summary,[tabindex]')) { el.setAttribute('tabindex', '-1'); el.classList.add('ppl-focus-target'); }
    el.focus({ preventScroll: true });
  }
  // Header-wide document handlers, registered once and applied to whichever header is on the page.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-ppl-header]').forEach(header => {
      const menu = header.querySelector('.menu');
      if (menu?.getAttribute('aria-expanded') !== 'true') return;
      header.querySelector('.navlinks')?.classList.remove('open'); menu.setAttribute('aria-expanded', 'false'); menu.setAttribute('aria-label', 'Open menu'); menu.focus();
    });
  });
  document.addEventListener('click', event => {
    if (event.target.closest?.('.shop-dropdown')) return;
    document.querySelectorAll('[data-ppl-header] .shop-menu').forEach(menu => { menu.hidden = true; menu.closest('.shop-dropdown')?.querySelector('.shop-toggle')?.setAttribute('aria-expanded', 'false'); });
  });
  // The announcement bar scrolls away; the sticky offset follows its real height.
  let topbarObserver = null;
  let observedBar = null;
  function syncTopbar() {
    const wrapper = document.querySelector('[data-ppl-review-header]')?.closest('.ppl-review-section');
    const bar = wrapper?.querySelector('.topbar');
    if (!bar) return;
    const set = () => wrapper.style.setProperty('--ppl-topbar-h', bar.offsetHeight + 'px');
    set();
    if (bar !== observedBar && typeof ResizeObserver === 'function') { topbarObserver?.disconnect(); observedBar = bar; topbarObserver = new ResizeObserver(set); topbarObserver.observe(bar); }
  }
  function openDialog(dialog, trigger) {
    if (!dialog) return;
    dialogs.set(dialog, trigger || document.activeElement);
    dialog.showModal();
    document.body.classList.add('dialog-open');
    const input = query(dialog, 'input');
    if (input) input.focus();
  }
  function setupDialog(dialog) {
    if (initialized.has(dialog)) return;
    initialized.add(dialog);
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); dialog.close(); }
    });
    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('video').forEach(v => v.pause());
      if (!document.querySelector('dialog[open]')) document.body.classList.remove('dialog-open');
      const trigger = dialogs.get(dialog);
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    });
    dialog.querySelectorAll('[data-dialog-close],button[aria-label^="Close"],.close-dialog,.review-story-close,.search-close').forEach(button => button.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
  }
  function setupCarousel(rail) {
    if (initialized.has(rail)) return;
    initialized.add(rail);
    const section = rail.closest('section');
    const kind = rail.dataset.pplCarousel;
    const selector = kind === 'product' ? '.product' : kind === 'review' ? '.review-card' : '.need-card';
    const controls = query(section, `[data-carousel-controls="${kind}"]`);
    if (!controls) return;
    const previous = query(controls, '[data-carousel-direction="-1"]');
    const next = query(controls, '[data-carousel-direction="1"]');
    const position = query(controls, '[data-carousel-position]');
    const cards = [...rail.querySelectorAll(selector)];
    function metrics() {
      const first = cards[0];
      if (!first) return { stride: 1, visible: 1, start: 0, scrollable: false };
      const gap = parseFloat(getComputedStyle(rail).columnGap) || parseFloat(getComputedStyle(rail).gap) || (kind === 'product' ? 14 : 19);
      const stride = first.getBoundingClientRect().width + gap;
      return { stride, visible: Math.max(1, Math.floor((rail.clientWidth + gap + 1) / stride)), start: Math.round(rail.scrollLeft / stride), scrollable: rail.scrollWidth > rail.clientWidth + 2 };
    }
    function update() {
      const m = metrics();
      if (kind === 'product') controls.hidden = !m.scrollable || cards.length < 2;
      const start = Math.min(cards.length - 1, Math.max(0, m.start));
      const of = position.dataset.carouselOf || 'of';
      const swipe = position.dataset.carouselSwipe ?? 'Swipe to explore';
      position.textContent = `${start + 1}${m.visible > 1 ? '–' + Math.min(cards.length, start + m.visible) : ''} ${of} ${cards.length}${kind === 'product' && m.visible === 1 && swipe ? ' · ' + swipe : ''}`;
      setArrows(previous, next, !m.scrollable || rail.scrollLeft < 2, !m.scrollable || rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2);
      if (kind === 'product') { if (m.scrollable) rail.setAttribute('tabindex', '0'); else rail.removeAttribute('tabindex'); }
    }
    function move(direction) {
      const m = metrics();
      rail.scrollBy({ left: direction * m.stride * (kind === 'product' ? 1 : m.visible), behavior: behavior() });
    }
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    rail.addEventListener('scroll', update, { passive: true });
    rail.addEventListener('keydown', event => {
      if (event.target === rail && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }
    });
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(update);
      observer.observe(rail);
      section.addEventListener('ppl:unload', () => observer.disconnect(), { once: true });
    }
    update();
  }
  function setupFormula(section) {
    if (initialized.has(section)) return;
    initialized.add(section);
    const tabs = [...section.querySelectorAll('[data-ingredient-pet]')];
    function select(tab, focus) {
      tabs.forEach(candidate => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(candidate.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
      if (focus) tab.focus();
    }
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(tab, false));
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        if (event.key === 'ArrowLeft') next = tabs[(index + tabs.length - 1) % tabs.length];
        if (event.key === 'Home') next = tabs[0];
        if (event.key === 'End') next = tabs[tabs.length - 1];
        if (next) { event.preventDefault(); select(next, true); }
      });
    });
    if (tabs[0]) select(tabs.find(t => t.getAttribute('aria-selected') === 'true') || tabs[0], false);
    section.querySelectorAll('.formula-reveal').forEach(button => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      query(button, 'span').textContent = '+';
      button.addEventListener('click', () => {
        panel.hidden = !panel.hidden;
        button.setAttribute('aria-expanded', String(!panel.hidden));
        query(button, 'span').textContent = panel.hidden ? '+' : '−';
      });
    });
  }
  function setupHeader(header) {
    if (initialized.has(header)) return;
    initialized.add(header);
    const navigation = query(header, '.navlinks');
    const menu = query(header, '.menu');
    const shop = query(header, '.shop-toggle');
    const shopMenu = query(header, '.shop-menu');
    function closeMenu() { navigation?.classList.remove('open'); menu?.setAttribute('aria-expanded', 'false'); menu?.setAttribute('aria-label', 'Open menu'); }
    function closeShop() { if (shopMenu) shopMenu.hidden = true; shop?.setAttribute('aria-expanded', 'false'); }
    menu?.addEventListener('click', event => {
      const open = menu.getAttribute('aria-expanded') !== 'true';
      navigation.classList.toggle('open', open); menu.setAttribute('aria-expanded', String(open)); menu.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      if (open && event.detail === 0) navigation.querySelector('a,button')?.focus();
    });
    header.querySelector('.shop-dropdown')?.addEventListener('focusout', event => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) closeShop();
    });
    shop?.addEventListener('click', () => { const open = shop.getAttribute('aria-expanded') !== 'true'; shop.setAttribute('aria-expanded', String(open)); shopMenu.hidden = !open; });
    header.querySelectorAll('a').forEach(a => a.addEventListener('click', () => { closeMenu(); closeShop(); }));
    header.addEventListener('keydown', event => {
      if (event.key === 'Escape') { closeShop(); if (event.target.closest('.shop-menu')) shop?.focus(); }
    });
    const searchButton = query(header, '.search-toggle');
    const searchDialog = document.querySelector('[data-ppl-search-dialog]');
    if (searchDialog && searchButton) {
      searchButton.addEventListener('click', () => { closeMenu(); closeShop(); openDialog(searchDialog, searchButton); });
    }
    const cart = query(header, '[data-ppl-cart]');
    cart?.addEventListener('click', event => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') { event.preventDefault(); drawer.open(cart); }
    });
  }
  function setupSearch(dialog) {
    const input = query(dialog, 'input');
    const results = query(dialog, '.search-results');
    const status = query(dialog, '.search-status');
    if (!input || !results || dialog.dataset.nativeSearch === 'true') return;
    const items = [
      ['Pawprint Protocol', 'Daily liquid for dogs', '#protocol-dog', 'dogs senior vitality energy nmn nad'],
      ['Wellness Protocol', 'Daily multivitamin for dogs', '#wellness-dog', 'dogs skin coat mobility joints multivitamin'],
      ['Ingredients', 'Explore product-specific ingredients', '#ingredients', 'ingredients coq10 resveratrol formula'],
      ['Pet-parent reviews', 'Read the Pawprint community’s stories', '#customer-reviews', 'reviews testimonials trustpilot'],
      ['Questions & support', 'Feeding, subscriptions and customer care', '#faq', 'help shipping feeding faq']
    ];
    function render() {
      const term = input.value.trim().toLowerCase();
      const matching = items.filter(item => (item[0] + ' ' + item[3]).toLowerCase().includes(term));
      results.replaceChildren();
      matching.forEach(([title, description, href]) => {
        const a = document.createElement('a'); const text = document.createElement('span'); const small = document.createElement('small'); const arrow = document.createElement('span');
        a.href = href; text.textContent = title; small.textContent = description; arrow.textContent = '↗'; text.append(small); a.append(text, arrow);
        a.addEventListener('click', () => dialog.close()); results.append(a);
      });
      if (status) status.textContent = matching.length ? `${matching.length} matching sections` : 'No matches. Try “cat”, “dog”, “ingredients” or “help”.';
    }
    input.addEventListener('input', render); render();
  }
  function initialize(scope = document) {
    syncTopbar();
    scope.querySelectorAll('[data-home-dialog]').forEach(setupDialog);
    scope.querySelectorAll('[data-ppl-header]').forEach(setupHeader);
    scope.querySelectorAll('[data-ppl-carousel]').forEach(setupCarousel);
    scope.querySelectorAll('[data-ppl-formulas]').forEach(setupFormula);
    scope.querySelectorAll('[data-ppl-search-dialog]').forEach(setupSearch);
    scope.querySelectorAll('[data-review]').forEach(button => {
      if (initialized.has(button)) return;
      initialized.add(button);
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.getAttribute('aria-controls')) || document.querySelector('[data-ppl-review-dialog]');
        const data = document.querySelector('[data-ppl-review-data]');
        if (!dialog || !data) return;
        const reviews = JSON.parse(data.textContent);
        const review = reviews[Number(button.dataset.review)];
        if (!review) return;
        query(dialog, '[data-review-title]').textContent = `${review.name}’s story`;
        query(dialog, '[data-review-meta]').textContent = review.meta;
        const quote = query(dialog, '[data-review-quote]'); quote.hidden = !review.quote; quote.textContent = review.quote ? `“${review.quote}”` : '';
        query(dialog, '[data-review-body]').textContent = review.body;
        query(dialog, '[data-review-source]').href = review.source;
        const summary = query(dialog, '[data-review-summary]'); if (summary) summary.textContent = 'Editorial summary of the source review';
        openDialog(dialog, button);
      });
    });
    scope.querySelectorAll('[data-ppl-trust]').forEach(section => {
      if (initialized.has(section)) return;
      initialized.add(section);
      const original = query(section, '.benefit-inner');
      if (!original) return;
      const marquee = document.createElement('div'); marquee.className = 'trust-marquee';
      const track = document.createElement('div'); track.className = 'trust-track';
      original.parentNode.insertBefore(marquee, original); marquee.append(track); track.append(original);
      const clone = original.cloneNode(true); clone.setAttribute('aria-hidden', 'true'); clone.inert = true;
      clone.querySelectorAll('a').forEach(a => a.tabIndex = -1); track.append(clone);
      const pauseIcon = '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M3 2h3v10H3zM8 2h3v10H8z"/></svg>';
      const playIcon = '<svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M4 2l8 5-8 5z"/></svg>';
      const pause = document.createElement('button'); pause.type = 'button'; pause.className = 'trust-pause'; pause.setAttribute('aria-label', 'Pause trust highlights'); pause.setAttribute('aria-pressed', 'false'); pause.innerHTML = pauseIcon;
      pause.addEventListener('click', () => { const paused = marquee.dataset.paused !== 'true'; marquee.dataset.paused = String(paused); pause.setAttribute('aria-pressed', String(paused)); pause.innerHTML = paused ? playIcon : pauseIcon; }); marquee.append(pause);
    });
  }
  function revealAnchor(hash, scroll = true, focus = false) {
    let id;
    try { id = decodeURIComponent(hash.replace(/^#/, '')); } catch { return; }
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    let ancestor = target;
    while (ancestor && ancestor !== document.body) {
      if (ancestor.tagName === 'DETAILS') ancestor.open = true;
      if (ancestor.hidden && ancestor.getAttribute('role') === 'tabpanel') {
        const tabs = document.querySelectorAll('[role="tab"]');
        const tab = [...tabs].find(t => t.getAttribute('aria-controls') === ancestor.id);
        tab?.click();
      }
      ancestor = ancestor.parentElement;
    }
    if (target.matches('[data-full-formula-card]')) { const disclosure = target.querySelector('[data-full-formula-disclosure]'); if (disclosure) disclosure.open = true; }
    const rail = target.closest('[data-ppl-carousel],[data-needs-rail]');
    if (rail && rail.scrollWidth > rail.clientWidth + 2) {
      const card = target.matches('.product,.review-card,.need-card') ? target : query(target, '.product,.review-card,.need-card');
      if (card) rail.scrollTo({ left: card.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft, behavior: behavior() });
    }
    if (scroll) target.scrollIntoView({ block: 'start', behavior: behavior() });
    if (focus) moveFocus(target);
  }
  document.addEventListener('click', event => {
    const anchor = event.target.closest('a[href]');
    if (!anchor || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) return;
    let destination;
    try { destination = new URL(anchor.href, location.href); } catch { return; }
    if (destination.origin !== location.origin || destination.pathname !== location.pathname || destination.search !== location.search || destination.hash.length < 2) return;
    let targetId;
    try { targetId = decodeURIComponent(destination.hash.slice(1)); } catch { return; }
    if (!document.getElementById(targetId)) return;
    event.preventDefault();
    if (location.hash !== destination.hash) history.pushState(null, '', destination.hash);
    revealAnchor(destination.hash, true, true);
  });
  window.addEventListener('hashchange', () => revealAnchor(location.hash, true, true));
  // The theme's skip link should land after the Pawprint header, not on it.
  function retargetSkipLink() {
    const skip = document.querySelector('a.skip-to-content-link[href="#MainContent"]');
    const after = document.querySelector('[data-ppl-review-header]')?.closest('.shopify-section')?.nextElementSibling;
    if (skip && after?.id) skip.setAttribute('href', '#' + after.id);
  }
  // The theme header group is hidden on this page; stop its scroll handler, which throws on every scroll without its search modal.
  if (document.querySelector('[data-ppl-review-header]') && window.customElements) {
    customElements.whenDefined('sticky-header').then(() => document.querySelectorAll('.shopify-section-group-header-group sticky-header').forEach(h => { if (h.onScrollHandler) window.removeEventListener('scroll', h.onScrollHandler, false); }));
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => { event.target.dispatchEvent(new Event('ppl:unload')); event.target.querySelectorAll('section').forEach(section => section.dispatchEvent(new Event('ppl:unload'))); });
  document.addEventListener('shopify:block:deselect', event => {
    const shopMenu = event.target.closest?.('.ppl-review-section .shop-menu');
    if (!shopMenu) return;
    const header = shopMenu.closest('[data-ppl-header]');
    shopMenu.hidden = true; header?.querySelector('.shop-toggle')?.setAttribute('aria-expanded', 'false');
    header?.querySelector('.navlinks')?.classList.remove('open'); header?.querySelector('.menu')?.setAttribute('aria-expanded', 'false');
  });
  document.addEventListener('shopify:block:select', event => {
    const block = event.target;
    if (!block.closest?.('.ppl-review-section')) return;
    const details = block.closest('details');
    if (details) details.open = true;
    const shopMenu = block.closest('.shop-menu');
    if (shopMenu) { const header = shopMenu.closest('[data-ppl-header]'); header?.querySelector('.navlinks')?.classList.add('open'); header?.querySelector('.menu')?.setAttribute('aria-expanded', 'true'); shopMenu.hidden = false; header?.querySelector('.shop-toggle')?.setAttribute('aria-expanded', 'true'); }
    const rail = block.closest('[data-ppl-carousel],[data-real-review-rail],.ugc-grid');
    if (rail && rail.scrollWidth > rail.clientWidth + 2) {
      const card = block.closest('.product,.review-card,.need-card,.ugc-card') || block;
      rail.scrollTo({ left: card.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft, behavior: 'instant' });
    }
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { initialize(); retargetSkipLink(); revealAnchor(location.hash); }, { once: true });
  else { initialize(); retargetSkipLink(); revealAnchor(location.hash); }
})();


/* Bridge the reviewed UI to native Shopify routes and editable story blocks. */
(() => {
  if(window.__pplR1009V2Bridge)return;window.__pplR1009V2Bridge=true;
  const opened=new WeakMap();
  function open(dialog,trigger){opened.set(dialog,trigger);dialog.showModal();(dialog.querySelector('input[type=search],input:not([type=hidden])')||dialog.querySelector('button'))?.focus();}
  function refreshCount(){const root=(window.Shopify?.routes?.root)||'/';fetch(root+'cart.js',{headers:{Accept:'application/json'}}).then(r=>r.json()).then(cart=>{document.querySelectorAll('[data-ppl-cart-count]').forEach(el=>el.textContent=cart.item_count||'');}).catch(()=>{});}
  let cartSubscribed=false;
  function init(){
    document.querySelectorAll('.ppl-review-section dialog').forEach(dialog=>{
      if(dialog.dataset.pplNativeBound)return;dialog.dataset.pplNativeBound='true';
      dialog.addEventListener('close',()=>{const trigger=opened.get(dialog);if(trigger?.isConnected)trigger.focus({preventScroll:true});});
    });
    refreshCount();
    if(!cartSubscribed){cartSubscribed=true;if(typeof subscribe==='function'&&typeof PUB_SUB_EVENTS!=='undefined'&&PUB_SUB_EVENTS.cartUpdate)subscribe(PUB_SUB_EVENTS.cartUpdate,refreshCount);document.addEventListener('cart:updated',refreshCount);}
  }
  document.addEventListener('click',event=>{
    const b=event.target.closest('button,a');if(!b?.closest('.ppl-review-section'))return;
    if(b.matches('.search-toggle')){
      event.preventDefault();event.stopImmediatePropagation();
      const header=b.closest('header');header.querySelector('.navlinks')?.classList.remove('open');header.querySelector('.menu')?.setAttribute('aria-expanded','false');
      const modal=document.getElementById('ppl-native-search');if(modal)open(modal,b);return;
    }
    if(b.matches('[data-ppl-search-close]')){event.stopImmediatePropagation();b.closest('dialog').close();return;}
    if(b.matches('[data-ppl-native-cart]')){
      const drawer=document.querySelector('cart-drawer');if(drawer&&typeof drawer.open==='function'){event.preventDefault();event.stopImmediatePropagation();drawer.open(b);}return;
    }
    if(b.matches('[data-ppl-story-body]')){
      event.preventDefault();event.stopImmediatePropagation();
      const d=document.getElementById('review-story');if(!d)return;
      d.querySelector('[data-review-title],#review-story-title').textContent=b.dataset.pplStoryName+'’s story';
      d.querySelector('[data-review-meta],#review-story-meta').textContent=b.dataset.pplStoryMeta;
      const q=d.querySelector('[data-review-quote],#review-story-quote');q.hidden=!b.dataset.pplStoryQuote;q.textContent=b.dataset.pplStoryQuote?'“'+b.dataset.pplStoryQuote+'”':'';
      d.querySelector('[data-review-body],#review-story-body').textContent=b.dataset.pplStoryBody;
      d.querySelector('[data-review-source],#review-story-source').href=b.dataset.pplStorySource;open(d,b);
    }
  },true);
  document.addEventListener('click',event=>{const d=event.target;if(d.matches('.ppl-review-section dialog')){const r=d.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)d.close();}});
  document.addEventListener('shopify:section:load',init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
})();

(() => {
  function ensureCtaArrows() {
    document.querySelectorAll('.ppl-review-section a.btn').forEach(button => {
      const slot = [...button.children].find(child => child.matches('span[aria-hidden="true"]'));
      if (!slot || slot.querySelector('svg[data-ppl-cta-arrow]')) return;
      const direction = button.classList.contains('btn-ingredients') || slot.textContent.trim() === '↓' ? 'down' : 'right';
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('width', '18');
      svg.setAttribute('height', '18');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '1.8');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('data-ppl-cta-arrow', direction);
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', direction === 'down' ? 'M10 4v12m-5-5 5 5 5-5' : 'M4 10h12m-5-5 5 5-5 5');
      svg.append(path);
      slot.classList.add('ppl-cta-arrow-slot');
      slot.replaceChildren(svg);
      button.setAttribute('data-ppl-cta-arrow-ready', direction);
    });
  }
  function removeDiagonalArrows() {
    document.querySelectorAll('.ppl-review-section').forEach(section => {
      const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT, {
        acceptNode: node => node.parentElement.closest('script,style,textarea') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
      });
      let node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.includes('↗')) node.nodeValue = node.nodeValue.replace(/[\s\u00a0]*↗/g, '');
      }
    });
    ensureCtaArrows();
  }
  document.addEventListener('shopify:section:load', removeDiagonalArrows);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', removeDiagonalArrows, { once: true });
  else removeDiagonalArrows();
})();
(() => {
  const bound = new WeakSet();
  function stabilizeShopPanels() {
    document.querySelectorAll('.ppl-review-section .shop .shop-carousel').forEach(carousel => {
      if (bound.has(carousel)) return;
      bound.add(carousel);
      const panels = [...carousel.querySelectorAll('[data-needs-panel]')];
      let width = 0;
      let height = 0;
      function measure() {
        const currentWidth = carousel.getBoundingClientRect().width;
        if (currentWidth !== width) {
          width = currentWidth;
          height = 0;
          carousel.style.removeProperty('--ppl-shop-panel-height');
        }
        const active = panels.find(panel => !panel.hidden);
        if (!active) return;
        const naturalHeight = Math.ceil(active.getBoundingClientRect().height);
        if (naturalHeight > height) {
          height = naturalHeight;
          carousel.style.setProperty('--ppl-shop-panel-height', height + 'px');
        }
      }
      new ResizeObserver(measure).observe(carousel);
      const observer = new MutationObserver(measure);
      panels.forEach(panel => observer.observe(panel, { attributes: true, attributeFilter: ['hidden'] }));
      measure();
    });
  }
  document.addEventListener('shopify:section:load', stabilizeShopPanels);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', stabilizeShopPanels, { once: true });
  else stabilizeShopPanels();
})();

/* Keep the shared header cart badge current after cart updates. */
(() => {
  function refreshCount() {
    const root = window.Shopify?.routes?.root || '/';
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(response => response.ok ? response.json() : Promise.reject())
      .then(cart => document.querySelectorAll('[data-ppl-cart-count]').forEach(el => { el.textContent = cart.item_count || ''; }))
      .catch(() => {});
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCount);
  window.addEventListener('pageshow', refreshCount);
})();

/* Preserve the store's existing privacy-preferences control in the shared footer. */
document.addEventListener('click', event => {
  const trigger = event.target.closest('[data-ppl-privacy-choice]');
  if (trigger && typeof window.showPreferences === 'function') {
    event.preventDefault();
    window.showPreferences(trigger);
  }
});

if (!window.__pplMarketSelectorsBound) {
  window.__pplMarketSelectorsBound = true;
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-ppl-market-select]');
    if (!select || !select.form) return;
    select.form.requestSubmit();
  });
}
